// Shared layout + validation core for the customer-facing quote form.
// Pure, framework-agnostic, zero runtime deps — safe to import in the
// embed bundle (no pg / redis / react). All three renderers
// (embed/index.ts, RequestPageClient.tsx, FormPreview.tsx) call these
// primitives so layout/validation logic lives in ONE place.
import {
  orderedFieldsForStep,
  isFieldVisible,
  type OrderedField,
  type FormStepId,
} from "@/lib/form-field-order";
import { resolveBuiltinField, resolveBuiltinLabel } from "@/lib/builtin-fields";
import { allowedCountries } from "@/lib/region-data";
import { isFileField, isMultiChoiceField, type CustomField } from "@/lib/shop-config-types";

// ── Row-grouping (pairing / trios) ──────────────────────────────────────────

export type PairedRow<T> =
  | { type: "single"; field: T }
  | { type: "pair"; fields: [T, T] }
  | { type: "trio"; fields: [T, T, T] };

/**
 * Group a pre-filtered, ordered list of fields into layout rows.
 *
 * The greedy algorithm (same logic that all three renderers had inlined):
 *  - Three consecutive ⅓-width fields → trio (3-col grid).
 *  - Two consecutive ½-width fields → pair (2-col grid).
 *  - Two consecutive ⅓-width fields (one of the trio was hidden) →
 *    pair rendered as ½+½ — the #200 single-country width-reflow fix.
 *  - Anything else (including a lone trailing half) → single (full width).
 *
 * `supportsThirds` — pass false for narrow contexts (modal/drawer) where
 * 3-col grids are too tight; thirds fall through to single-column there.
 * Defaults to true.
 */
export function pairRowsForStep<T extends { column_width: "full" | "half" | "third" }>(
  rows: T[],
  opts?: { supportsThirds?: boolean },
): PairedRow<T>[] {
  const supportsThirds = opts?.supportsThirds ?? true;
  const result: PairedRow<T>[] = [];
  let i = 0;
  while (i < rows.length) {
    const a = rows[i];
    const b = rows[i + 1];
    const c = rows[i + 2];
    if (supportsThirds && a.column_width === "third" && b?.column_width === "third" && c?.column_width === "third") {
      result.push({ type: "trio", fields: [a, b!, c!] });
      i += 3;
    } else if (a.column_width === "half" && b?.column_width === "half") {
      result.push({ type: "pair", fields: [a, b!] });
      i += 2;
    } else if (supportsThirds && a.column_width === "third" && b?.column_width === "third") {
      // Two surviving thirds (the third partner was hidden/filtered) → ½+½ reflow
      result.push({ type: "pair", fields: [a, b!] });
      i += 2;
    } else {
      result.push({ type: "single", field: a });
      i += 1;
    }
  }
  return result;
}

// ── Field resolution ─────────────────────────────────────────────────────────

// Minimal structural type accepted by resolveFormLayout / validateFormState.
// Covers ShopConfig, PublicShopConfig, and the embed's inlined ShopConfig.
export interface FormCoreConfig {
  builtin_fields?: Partial<Record<string, {
    required?: boolean;
    helper?: string;
    label?: string;
    step?: string;
    display_order?: number;
    column_width?: string;
    show_when?: unknown;
  }>>;
  custom_fields?: CustomField[];
  require_shipping?: boolean;
  form_steps?: unknown;
  allowed_countries?: string[];
  consent_enabled?: boolean;
  consent_required?: boolean;
}

/**
 * Resolve the visible, ordered field list for a form step.
 * Applies in sequence:
 *   1. orderedFieldsForStep — ordered merged list of built-ins + customs
 *   2. show-when filter (if formValues provided) — hides conditional fields
 *   3. single-country hide — drops the country picker when only one is offered
 */
export function resolveFormLayout(
  config: FormCoreConfig,
  step: FormStepId,
  formValues?: Record<string, string | string[] | undefined>,
): OrderedField[] {
  const singleCountry = allowedCountries(config.allowed_countries).length === 1;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let rows = orderedFieldsForStep(config as any, step);
  if (formValues !== undefined) {
    rows = rows.filter(r => isFieldVisible(r, formValues));
  }
  rows = rows.filter(r => !(r.kind === "builtin" && r.key === "country" && singleCountry));
  return rows;
}

// ── Validation ───────────────────────────────────────────────────────────────

export interface ValidateBuiltinValues {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  preferred_contact: string;
  address: string;
  street_address_2: string;
  city: string;
  country: string;
  state: string;
  zip: string;
}

export interface ValidateExtras {
  consentAccepted?: boolean;
  billingSame?: boolean;
  billingAddress1?: string;
  billingCity?: string;
  billingZip?: string;
  /** Everything the customer has attached so far, tagged or not. The only
   *  evidence a required `file` question has been answered. */
  attachments?: CustomFieldAttachment[];
}

function getBuiltinValue(key: string, values: ValidateBuiltinValues): string {
  return (values as unknown as Record<string, string>)[key] ?? "";
}

/** The shape every surface can supply for an in-flight or persisted
 *  attachment. The embed's `Attachment`, the hosted page's upload state,
 *  and a `quote_request_files` row all satisfy it structurally. */
export interface CustomFieldAttachment {
  field_id?: string | null;
  /** Absent ⇒ already persisted, i.e. done. Only the two customer-facing
   *  surfaces carry an in-flight status. */
  status?: "uploading" | "done" | "error";
}

/** "Answered" per control cardinality: a non-empty ARRAY for `checkbox`
 *  (legacy `multiselect`), a non-empty STRING for `dropdown` / `radio`
 *  (legacy `select`) / `text` / `textarea`.
 *
 *  A `file` field is answered when at least one FINISHED attachment
 *  carries its id. Its answer is never in `value` — mirroring filenames
 *  into the values map would persist a second copy of the filename into
 *  `services` JSONB alongside `quote_request_files`. Callers pass what
 *  they have; a caller with no attachment list reports a `file` field
 *  unanswered, which is the safe direction (a required question blocks
 *  submit rather than passing on a file nobody uploaded). That is also
 *  what makes a RESUMED DRAFT correct: drafts deliberately drop
 *  attachments, so a required `file` field looks unanswered until the
 *  customer re-attaches.
 *
 *  Exported because the hosted request page used to carry its own copy of
 *  this check, which then had to be remembered every time the type union
 *  moved. One definition, three surfaces. A field hidden by `show_when` is
 *  never reached here — visibility is filtered upstream in
 *  validateFormState, so a hidden required field cannot block submit. */
export function isCustomFilled(
  field: CustomField,
  value: string | string[] | undefined,
  attachments?: CustomFieldAttachment[],
): boolean {
  if (isFileField(field.type)) {
    if (!Array.isArray(attachments)) return false;
    return attachments.some(
      a => a && a.field_id === field.id && (a.status === undefined || a.status === "done"),
    );
  }
  if (isMultiChoiceField(field.type)) return Array.isArray(value) && value.length > 0;
  return typeof value === "string" && value.trim().length > 0;
}

function isValidEmail(v: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());
}

/**
 * Validate fields on a form step. Returns error message strings.
 *
 * Shared between the embed and the hosted request page so both surfaces
 * show identical errors. Applies the same rules as the three renderers
 * had inline:
 *  - built-in required + email format
 *  - custom field required
 *  - consent checkbox (finish step, when enabled + required)
 *  - billing address completeness (shipping step, when separate billing)
 *
 * The `formValues` passed to resolveFormLayout come from spreading
 * `builtinValues` + `customValues`, matching the embed's
 * currentFormValues() and RequestPageClient's formValues memo.
 */
export function validateFormState(
  step: FormStepId,
  config: FormCoreConfig,
  builtinValues: ValidateBuiltinValues,
  customValues: Record<string, string | string[] | undefined>,
  extras?: ValidateExtras,
): string[] {
  const errs: string[] = [];
  const formValues: Record<string, string | string[] | undefined> = {
    first_name: builtinValues.first_name,
    last_name: builtinValues.last_name,
    email: builtinValues.email,
    phone: builtinValues.phone,
    company: builtinValues.company,
    preferred_contact: builtinValues.preferred_contact,
    address: builtinValues.address,
    street_address_2: builtinValues.street_address_2,
    city: builtinValues.city,
    country: builtinValues.country,
    state: builtinValues.state,
    zip: builtinValues.zip,
    ...customValues,
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cfg = config as any;
  const rows = resolveFormLayout(config, step, formValues);

  for (const row of rows) {
    if (row.kind === "builtin") {
      const k = row.key;
      const bf = resolveBuiltinField(k, cfg);
      const v = getBuiltinValue(k, builtinValues);
      if (k === "email") {
        if (bf.required && !v.trim()) errs.push(`${resolveBuiltinLabel(k, cfg)} is required.`);
        else if (v.trim() && !isValidEmail(v)) errs.push("Email doesn't look right.");
      } else {
        if (bf.required && !v.trim()) errs.push(`${resolveBuiltinLabel(k, cfg)} is required.`);
      }
    } else {
      const f = row.field;
      if (!f.required) continue;
      if (!isCustomFilled(f, customValues[f.id], extras?.attachments)) errs.push(`${f.label} is required.`);
    }
  }

  if (step === "finish") {
    // Consent lives on Finish (above Submit) so the gate blocks the
    // final submit, not the step-1→2 transition.
    if (config.consent_enabled && config.consent_required !== false && !extras?.consentAccepted) {
      errs.push("Please confirm consent to continue.");
    }
  }

  if (step === "shipping" && extras?.billingSame === false) {
    if (!extras.billingAddress1?.trim() || !extras.billingCity?.trim() || !extras.billingZip?.trim()) {
      errs.push("Enter the billing street address, city, and ZIP — or check “Billing address is the same as shipping.”");
    }
  }

  return errs;
}
